import {DeepGet, DeepSet} from "js-vextensions";
import {EngineConfig} from "../../../../Server/DB/engineConfigs/@EngineConfig.js";
import {EngineConfigVariant} from "../../../../Server/DB/engineConfigs/@EngineConfig/@Shared/EngineConfigVariant.js";

/** Returns the full variant chain rooted at `name` (ancestors first, `name` last), with cycle prevention. */
export function GetVariantChain(name: string, allVariants: EngineConfigVariant[], visited = new Set<string>()): EngineConfigVariant[] {
	if (visited.has(name)) return []; // cycle prevention
	visited.add(name);
	const v = allVariants.find(a=>a.name == name);
	if (v == null) return [];
	const chain: EngineConfigVariant[] = [];
	for (const inheritName of (v.inheritVariants ?? [])) {
		chain.push(...GetVariantChain(inheritName, allVariants, visited));
	}
	chain.push(v);
	return chain;
}

/**
 * Returns the value that `fieldPath` would have after applying the base config and all of `variant`'s
 * inherited ancestors' overrides — but before applying `variant`'s own overrides.
 * Also returns the `source`: `"base-config"` if no ancestor override contributed, or the name of the
 * last ancestor variant whose override set the value.
 * Useful for detecting redundant field-overrides.
 */
export function GetPreOverrideValue(config: EngineConfig, variant: EngineConfigVariant, fieldPath: string): {value: any, source: "base-config" | string} {
	const allVariants = config.variants.variants;
	const inheritChain: EngineConfigVariant[] = [];
	for (const iName of (variant.inheritVariants ?? [])) inheritChain.push(...GetVariantChain(iName, allVariants));
	let val = DeepGet(config, fieldPath, null, ".");
	let source: "base-config" | string = "base-config";
	for (const cv of inheritChain) {
		const match = cv.fieldOverrides.find(o=>o.fieldPath == fieldPath);
		if (match != null) { val = match.value; source = cv.name; }
	}
	return {value: val, source};
}

/**
 * Applies the field-overrides for the named variant (and its full inheritance chain) onto `config_final`.
 * Also records the original values of overridden fields into `fieldOverrides_origValues`.
 * Fields whose path starts with "__" or "#" are treated as commented-out and skipped.
 */
export function ApplyVariantOverrides(variantName: string, config_orig: EngineConfig, config_final: EngineConfig, fieldOverrides_origValues: {[fieldPath: string]: any}): void {
	const variant = config_orig.variants.variants.find(a=>a.name == variantName);
	if (variant == null) return;
	const variantChain = GetVariantChain(variantName, config_orig.variants.variants);
	for (const chainVariant of variantChain) {
		for (const fieldOverride of chainVariant.fieldOverrides) {
			// if field-path starts with "__" or "#", interpret it as being "commented out" (ie. disabled)
			if (fieldOverride.fieldPath.startsWith("__") || fieldOverride.fieldPath.startsWith("#")) continue;

			// store original values of fields that have overrides (so that raw config can be discerned later, from stored session data)
			if (!Object.getOwnPropertyNames(fieldOverrides_origValues).includes(fieldOverride.fieldPath)) {
				fieldOverrides_origValues[fieldOverride.fieldPath] = DeepGet(config_orig, fieldOverride.fieldPath, null, ".");
			}
			DeepSet(config_final, fieldOverride.fieldPath, fieldOverride.value, ".", false);
		}
	}
}